///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Route, Switch } from "react-router-dom";
import { Tab, TabList } from "react-tabs";
import { FormattedMessage } from "react-intl";

import ActiveBidsCardView from "../components/ActiveBidsCardView";
import ActiveBidsGridView from "../components/ActiveBidsGridView";
import HomePageSearch from "../components/HomePageSearch";
import CardIcon from "../components/icons/CardIcon";
import GridIcon from "../components/icons/GridIcon";
import { fetchActiveBids, filterActiveBids } from "../slices/activeBidsSlice";
import SystemError from "../components/SystemError";
import messages from "../helpers/messages";
import Filter from "../components/home/Filter";
import { userRole, userRoleListForActiveBids } from "../helpers/types";
import AutoComplete from "../components/form-controls/AutoComplete";
import { setUserRoleForManagementUser } from "../slices/userSlice";

const Home = ({ history }) => {
  const dispatch = useDispatch();
  const activeBids = useSelector(state => state.activeBids);
  const { user } = useSelector(state => state.user);
  const { enableInternalCapacityMatching } = useSelector(
    state => state.featureFlags
  );
  const route = history.location.pathname;

  useEffect(() => {
    dispatch(fetchActiveBids());
  }, [dispatch]);

  const reloadPage = () => {
    dispatch(fetchActiveBids());
  };

  const switchRoute = newRoute => {
    history.push(newRoute);
    dispatch(filterActiveBids({ user, cardView: newRoute === "/" }));
  };

  return (
    <>
      {activeBids.isFailure ? (
        <SystemError reloadPage={reloadPage} />
      ) : (
        <div id="home-page" className="p-24 pt-16" data-testid="home">
          <h1 id="home-title" className="alpha-heading inline-block">
            {messages.rfqManager.defaultMessage}
          </h1>
          <div className="fr">
            {user.role === userRole.management && (
              <div
                className="inline-block active-bids mr-12"
                data-testid="userRoleToView"
              >
                <AutoComplete
                  label={messages.userRoleToView.defaultMessage}
                  staticOptions={
                    enableInternalCapacityMatching
                      ? userRoleListForActiveBids
                      : userRoleListForActiveBids.filter(
                          item =>
                            item.label !==
                              messages.carrierRelationsRepresentative
                                .defaultMessage &&
                            item.label !==
                              messages.procurementRepresentative.defaultMessage
                        )
                  }
                  value={userRoleListForActiveBids.find(
                    x => x.value === user.userRoleForManagementUser
                  )}
                  handleChange={userRole => {
                    dispatch(setUserRoleForManagementUser(userRole.value));
                    dispatch(fetchActiveBids());
                  }}
                />
              </div>
            )}
            <HomePageSearch
              user={user}
              value={activeBids.filters.searchTerm}
              cardView={route === "/"}
            />
          </div>
          <div className="mb-8 mt-12">
            <h1 className="beta-heading mb-0 flex-none inline-block">
              <FormattedMessage
                {...messages.activeBidsCount}
                values={{ activeBidsCount: activeBids.totalCount }}
              />
            </h1>
            <div className="text-right fr">
              <Filter cardView={route === "/"} user={user} />
              <TabList className="mb-0 inline-block">
                <Tab
                  selected={route === "/"}
                  onClick={() => switchRoute("/")}
                  data-testid="card-view"
                >
                  <CardIcon
                    height="16"
                    width="16"
                    fill={route === "/" ? "#fff" : "#000"}
                  />
                </Tab>
                <Tab
                  selected={route === "/grid-view"}
                  onClick={() => switchRoute("/grid-view")}
                  data-testid="grid-view"
                >
                  <GridIcon
                    height="10"
                    width="14"
                    fill={route === "/grid-view" ? "#fff" : "#343434"}
                    className="mt-4 mb-2 mx-1"
                  />
                </Tab>
              </TabList>
            </div>
          </div>
          <Switch>
            <Route
              exact
              path="/"
              render={() => <ActiveBidsCardView activeBids={activeBids} />}
            />
            <Route
              path="/grid-view"
              render={() => <ActiveBidsGridView activeBids={activeBids} />}
            />
          </Switch>
        </div>
      )}
    </>
  );
};

export default Home;
